import { Object3D, Vector3 } from "three";
import { ISpawnable } from "./ISpawnable";
import { Event } from "event-system";
import { Ticker } from "pixi.js";
import { ISpawnerConfiguration } from "./ISpawnerConfiguration";
import { GameElement } from "../../../models/field/GameElement";
import { GameResourcesPackage } from "../../../resources/GameResourcesPackage";
import { AudioController } from "../../AudioController";
import { MoleHole } from "../gamefield/mole/MoleHole";
import { Portal } from "../gamefield/portal/Portal";
import { GameViewModel } from "../../../models/GameViewModel";
import { GameView3d } from "../../../views/GameView3d";
import { Character } from "../character/Character";
import { Mine } from "../gamefield/mines/Mine";
import { IMinable } from "../gamefield/mines/IMinable";
import { Box } from "../gamefield/box/Box";
import { GameController } from "../../../controllers/GameController";
import { Vegetable } from "../gamefield/Vegetable";
import { FontResourcesPackage } from "../../../../../common/resources/FontResourcesPackage";
import { LocalizationModel } from "../../../../../common/models/LocalizationModel";
import { BatchedRenderer } from "three.quarks";

export class Spawner extends Object3D implements IMinable {
    public readonly isMinable: boolean = true;
    private startTimeOffsetMS: number;
    private timeToNextSpawnMS: number;
    private spawnedItem: ISpawnable;
    private presetSpawnableItems: Array<GameElement>;
    private vegetable: Vegetable;
    public get tractorVegetable(): Vegetable {
        return (this.vegetable && this.vegetable.alive) ? this.vegetable : null;
    }

    public onSpawned: Event<ISpawnable>;
    public onDestroyed: Event<ISpawnable>;

    public get occupied(): boolean {
        return this.spawnedItem != null;
    }
    public get occupiedWithBox(): boolean {
        return this.spawnedItem != null && (this.spawnedItem instanceof Box);
    }
    public get withVegetable(): boolean {
        return this.vegetable != null;
    }
    private _mine: Mine;
    public get mine(): Mine {
        return this._mine;
    }
    public set mine(value: Mine) {
        this._mine = value;
        if (value != null) {
            this._mine.exploded.once(this.onMineExploded, this);
            this._mine.position.set(0, 5, 0);
            this.add(this._mine);
            // Need to do it for Physics to work properly
            this._mine.updateMatrixWorld(true);
        }
    }
    public get mined(): boolean {
        return this._mine != null;
    }

    public get withBox(): boolean {
        return this.spawnedItem instanceof Box;
    }

    public constructor(
        private readonly spawnerConfiguration: ISpawnerConfiguration,
        private readonly gameResources: GameResourcesPackage,
        private readonly fontResources: FontResourcesPackage,
        private readonly particlesRenderer: BatchedRenderer,
        private readonly localization: LocalizationModel,
        private readonly gameViewModel: GameViewModel,
        private readonly gameView3d: GameView3d,
        private readonly gameController: GameController,
        private readonly character: Character,
        private readonly audioController: AudioController,
        public readonly fieldIndex: number
    ) {
        super();
        this.validateSpawnerConfig();
        this.timeToNextSpawnMS = this.spawnerConfiguration.spawnIntervalMS;
        this.presetSpawnableItems = this.spawnerConfiguration.presetSpawnableItems ? [...this.spawnerConfiguration.presetSpawnableItems] : [];
        this.onSpawned = new Event();
        this.onDestroyed = new Event();
        this.createVegetable();
    }

    public start(startTimeOffsetMS: number = 0): void {
        this.startTimeOffsetMS = startTimeOffsetMS;
        Ticker.shared.add(this.onTick, this);
    }

    public stop(): void {
        Ticker.shared.remove(this.onTick, this);
    }

    public createVegetable(): void {
        if (this.spawnerConfiguration.withVegetable) {
            this.vegetable = new Vegetable(this.gameResources);
            this.vegetable.position.add(this.getVegetableSpawnPoint());
            this.vegetable.eaten.add(this.onVegetableEaten, this);
            this.add(this.vegetable);
        }
    }

    public restoreVegetable(): void {
        this.vegetable.restore();
    }

    private onTick(_dt: number): void {
        this.startTimeOffsetMS = Math.max(this.startTimeOffsetMS - Ticker.shared.deltaMS, 0);
        if (this.startTimeOffsetMS > 0) {
            return;
        }
        if (this.spawnedItem != null) {
            return;
        }
        this.timeToNextSpawnMS = Math.max(this.timeToNextSpawnMS - Ticker.shared.deltaMS, 0);
        if (this.timeToNextSpawnMS == 0) {
            this.timeToNextSpawnMS = this.spawnerConfiguration.spawnIntervalMS;
            this.spawn();
        }
    }

    public forceSpawn(): void {
        if (this.spawnedItem != null) {
            return;
        }

        let gameElementToGenerate: GameElement = null;

        if (this.presetSpawnableItems.length != 0) {
            // Get item from config
            gameElementToGenerate = this.presetSpawnableItems.shift() as GameElement;
        } else {
            const allPossibleElementsToForce = [GameElement.EatingMole, GameElement.AttackingMole, GameElement.AggressiveEaterMole];
            const possibleMolesToSpawn = this.spawnerConfiguration.generalSpawnConfig.filter(config => allPossibleElementsToForce.includes(config.gameElement) && config.chance != 0).map(config => config.gameElement).shuffle();
            gameElementToGenerate = possibleMolesToSpawn[0];
        }

        if (gameElementToGenerate == null) {
            console.warn("Spawner try to forceSpawn, but can't spawn null!");
            return;
        }
        this.createGameElement(gameElementToGenerate);
    }

    private spawn(): void {
        if (this.spawnedItem != null) {
            return;
        }
        let chance = Math.random();
        if (chance > this.spawnerConfiguration.spawnChance) {
            return;
        }

        let gameElementToGenerate: GameElement = null;

        if (this.presetSpawnableItems.length != 0) {
            // Get item from config
            gameElementToGenerate = this.presetSpawnableItems.shift() as GameElement;
        } else {
            // Generate random element
            chance = Math.random();
            let cumulativeChance = 0;
            for (const spawnableItem of this.spawnerConfiguration.generalSpawnConfig) {
                cumulativeChance += spawnableItem.chance;
                if (chance <= cumulativeChance) {
                    gameElementToGenerate = spawnableItem.gameElement;
                    break;
                }
            }
        }
        if (gameElementToGenerate == null) {
            return;
        }
        this.createGameElement(gameElementToGenerate);
    }

    private createGameElement(gameElement: GameElement): void {
        switch (gameElement) {
            case GameElement.Portal:
                this.spawnedItem = new Portal(this.gameResources, this.fontResources, this.particlesRenderer, this.localization, this.gameViewModel, this.gameView3d, this.character, this.audioController, this.gameController, 5000, 3, 3, this.fieldIndex);
                break;
            case GameElement.AggressiveEaterMole:
            case GameElement.AttackingMole:
            case GameElement.EatingMole:
            case GameElement.TutorialMole:
                this.spawnedItem = new MoleHole(this.gameResources, this.fontResources, this.localization, gameElement, this.gameView3d, this.particlesRenderer, this.character, this.vegetable, this.audioController, this.gameController, this.fieldIndex);
                break;
            case GameElement.WeaponsBox:
            case GameElement.BoosterBox:
            case GameElement.HealBox:
                if (this.mined) {
                    return;
                }
                this.spawnedItem = new Box(this.gameResources, this.gameController, this.audioController, this.particlesRenderer, gameElement, this.fieldIndex, 10000);
                break;
        }
        this.spawnedItem.onCanBeDestroyed.once(this.despawn, this);
        this.add(this.spawnedItem);
        this.spawnedItem.updateMatrixWorld(true);
        this.onSpawned.emit(this.spawnedItem);
    }

    private despawn(_item: ISpawnable) {
        this.remove(this.spawnedItem);
        this.spawnedItem.destroy();
        this.onDestroyed.emit(this.spawnedItem);
        this.spawnedItem = null;
    }

    private validateSpawnerConfig() {
        let globalProbability = 0;
        for (const spawnableItem of this.spawnerConfiguration.generalSpawnConfig) {
            globalProbability += spawnableItem.chance;
            globalProbability = Math.round(globalProbability * 1000) / 1000;
        }
        if (globalProbability > 1 || globalProbability < 0) {
            throw new Error("Wrong generalSpawnConfig. Sum of all element chances should be in interval [0, 1]");
        }
    }

    private getVegetableSpawnPoint(): Vector3 {
        const angle = -Math.PI * 0.2 + Math.random() * -Math.PI * 0.6;
        const distance = 1.5;

        const xOffset = Math.cos(angle) * distance;
        const zOffset = Math.sin(angle) * distance;

        return new Vector3(xOffset, 0, zOffset);
    }

    private onVegetableEaten(): void {
        this.gameController.handleVegetableEaten();
    }

    public onMineExploded(mine: Mine): void {
        this.remove(mine);
        this.mine = null;
    }

    public clearMine(): void {
        this.mined && this._mine.destroy();
    }

    public destroy(): void {
        if (this.vegetable != null) {
            this.vegetable.eaten.remove(this.onVegetableEaten, this);
        }
        this.stop();
    }
}